import { Icon } from '@iconify/react/dist/iconify.js';
import { Checkbox, Modal, TextInput } from '@mantine/core';
import { toast } from '../../../notifications/notifications';
import { useMemo, useState } from 'react';
import { useAssignUsersToGroup } from '../../../../api/rbac-client/rbac-client.hooks';
import type { OrganizationUser, PermissionGroup } from '../../../../api/rbac-client/rbac-client.type';
import { secondaryBase } from '../../../../styles/design-tokens';
import { Button } from '../../../../ui-library/button/button';
import { Text } from '../../../../ui-library/typography/typography';
import { ModalActions, ModalDivider, ModalGroupSection, ModalGroupTitle } from './groups-tab.styles';

export function AddUserModal({
  opened,
  onClose,
  organizationId,
  orgUsers,
  groups,
}: {
  opened: boolean;
  onClose: () => void;
  organizationId: string;
  orgUsers: OrganizationUser[];
  groups: PermissionGroup[];
}) {
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [selectedGroupTypes, setSelectedGroupTypes] = useState<Set<string>>(new Set());
  const [userSearch, setUserSearch] = useState('');
  const [groupSearch, setGroupSearch] = useState('');
  const assignMutation = useAssignUsersToGroup();

  const filteredUsers = useMemo(() => {
    if (!userSearch.trim()) return orgUsers;
    const query = userSearch.toLowerCase();
    return orgUsers.filter(
      (user) => user.name.toLowerCase().includes(query) || user.email.toLowerCase().includes(query),
    );
  }, [orgUsers, userSearch]);

  const filteredGroups = useMemo(() => {
    if (!groupSearch.trim()) return groups;
    const query = groupSearch.toLowerCase();
    return groups.filter((group) => group.group_type_display.toLowerCase().includes(query));
  }, [groups, groupSearch]);

  function toggleEmail(email: string, checked: boolean) {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (checked) next.add(email);
      else next.delete(email);
      return next;
    });
  }

  function toggleGroupType(groupType: string, checked: boolean) {
    setSelectedGroupTypes((prev) => {
      const next = new Set(prev);
      if (checked) next.add(groupType);
      else next.delete(groupType);
      return next;
    });
  }

  function handleClose() {
    setSelectedEmails(new Set());
    setSelectedGroupTypes(new Set());
    setUserSearch('');
    setGroupSearch('');
    onClose();
  }

  async function handleSubmit() {
    if (selectedEmails.size === 0 || selectedGroupTypes.size === 0) return;

    try {
      const results = await Promise.all(
        [...selectedGroupTypes].map((groupType) =>
          assignMutation.mutateAsync({
            organization_id: organizationId,
            group_type: groupType,
            user_emails: [...selectedEmails],
          }),
        ),
      );

      const totalFailed = results.reduce((sum, r) => sum + r.failed.length, 0);
      if (totalFailed > 0) {
        toast.warning(`Some users could not be assigned. ${totalFailed} assignment(s) failed.`);
      } else {
        toast.success('Users assigned to groups successfully.');
      }
      handleClose();
    } catch {
      toast.error('Failed to assign users to groups.');
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      withCloseButton
      centered
      size="md"
      padding="md"
      zIndex={1000}
      styles={{ header: { padding: '8px 12px 0', minHeight: 'unset' }, close: { marginTop: 4 } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Icon icon="material-symbols:person-add" width={28} color={secondaryBase} />
        <Text weight="bold" size="large">
          Add User to Group
        </Text>
      </div>

      <ModalDivider />

      <ModalGroupSection>
        <ModalGroupTitle>Users</ModalGroupTitle>
        <TextInput
          placeholder="Search users..."
          value={userSearch}
          onChange={(event) => setUserSearch(event.currentTarget.value)}
          style={{ marginBottom: 8 }}
        />
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {filteredUsers.map((user) => (
            <Checkbox
              key={user.id}
              label={user.name || user.email}
              checked={selectedEmails.has(user.email)}
              onChange={(event) => toggleEmail(user.email, event.currentTarget.checked)}
              style={{ marginBottom: 6 }}
            />
          ))}
        </div>
      </ModalGroupSection>

      <ModalGroupSection>
        <ModalGroupTitle>Groups</ModalGroupTitle>
        <TextInput
          placeholder="Search groups..."
          value={groupSearch}
          onChange={(event) => setGroupSearch(event.currentTarget.value)}
          style={{ marginBottom: 8 }}
        />
        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {filteredGroups.map((group) => (
            <Checkbox
              key={group.id}
              label={group.group_type_display}
              checked={selectedGroupTypes.has(group.group_type)}
              onChange={(event) => toggleGroupType(group.group_type, event.currentTarget.checked)}
              style={{ marginBottom: 6 }}
            />
          ))}
        </div>
      </ModalGroupSection>

      <ModalDivider />

      <ModalActions>
        <Button variant="outline" size="sm" radius="xl" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          radius="xl"
          onClick={handleSubmit}
          loading={assignMutation.isPending}
          disabled={selectedEmails.size === 0 || selectedGroupTypes.size === 0}
        >
          Add Users
        </Button>
      </ModalActions>
    </Modal>
  );
}
